import { Paper, PaperProps } from '@mui/material'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { shadows } from 'c1ui/theme/C1Theme'
import { C1Link } from 'components/core/links/C1Link'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { Route } from 'lib/routes'
import { FC, KeyboardEvent, MouseEvent } from 'react'

export interface C1CardProps extends PaperProps {
    /** Type-safe route to navigate to. Renders the card as a Next.js link with hover and focus styles. */
    href?: Route
    /** Highlights the card with a primary border and background to indicate active/current selection. */
    isSelected?: boolean
    /** Accessible label for the card, applied as `aria-label`. Required when the card is clickable and has no visible text label. */
    ariaLabel?: string
    /** When true, suppresses `role="button"` and keyboard handling so nested interactive elements (buttons, links) work correctly. */
    hasInteractiveChildren?: boolean
}

const baseCardSx = {
    padding: 4,
    marginBottom: 4,
    borderRadius: C1BorderRadius.large,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    color: 'text.primary',
    boxShadow: shadows.default,
    transition: 'border-color 0.2s, background-color 0.2s, box-shadow 0.2s',
}

/**
 * Bordered surface container for grouping related content.
 *
 * Wraps MUI Paper with C1 design tokens (border, border-radius, shadow) and optional
 * interactivity. Use C1Card anywhere you need a visually distinct content region —
 * dashboard stats, detail panels, selection tiles, or navigable list items.
 *
 * ## Modes
 * - **Static** — no `href` or `onClick`. Renders a plain `<div>` paper with no hover/focus styles.
 * - **Link** — pass `href` with a type-safe `Route`. Renders as a Next.js `C1Link` so the card
 *   is a real anchor element with full keyboard navigation and right-click support.
 * - **Button** — pass `onClick` without `href`. Renders as a `<div role="button">` with
 *   `Enter`/`Space` keyboard support. Set `hasInteractiveChildren` when the card contains
 *   its own buttons or links to avoid nested-interactive-element a11y violations.
 *
 * ## Selected state
 * Pass `isSelected` to apply the active treatment (primary border + tinted background).
 * The card sets `data-active` and `aria-current` so downstream styles and screen readers
 * can react to the selection.
 *
 * ## Styling notes
 * - Default padding is `theme.spacing(4)` (32 px) and bottom margin is `theme.spacing(4)`.
 *   Override with `sx` — many consumers set `sx={{ mb: 0, p: 2 }}` for tighter layouts.
 * - Uses `C1BorderRadius.large` (10 px) and the C1 shadow tokens (`shadows.default` / `shadows.strong`).
 * - The `sx` prop is merged **after** internal styles via `sxc`, so consumer overrides always win.
 *
 * @designSystem
 * @category atoms
 * @relatedComponent C1SelectCard | Use for radio-style selection tiles with title, description, and icon
 * @externalReference MUI Paper API | https://mui.com/material-ui/api/paper/
 */
export const C1Card: FC<C1CardProps> = ({
    children,
    sx,
    href,
    onClick,
    isSelected,
    tabIndex,
    ariaLabel,
    hasInteractiveChildren = false,
    ...props
}) => {
    const isClickable = href || onClick

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
        if (!onClick || href) return
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick(e as unknown as MouseEvent<HTMLDivElement>)
        }
    }

    const cardSx = {
        ...baseCardSx,
        ...(isClickable && {
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
                borderColor: 'primary.main',
                boxShadow: shadows.strong,
            },
            '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
                boxShadow: (theme) => `${shadows.default}, inset 0 0 0 1px ${theme.palette.primary.dark}`,
            },
        }),
        '&[data-active]': {
            borderColor: 'primary.main',
            backgroundColor: 'primary.extraLight',
            '&:hover': {
                boxShadow: (theme) => `${shadows.default}, inset 0 0 0 1px ${theme.palette.primary.dark}`,
            },
            '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.dark',
                outlineOffset: '2px',
            },
        },
    }

    const baseProps = {
        sx: sxc(cardSx, sx),
        ...(isSelected && {
            'data-active': '',
            'aria-current': true as const,
        }),
        ...(ariaLabel && { 'aria-label': ariaLabel }),
        ...props,
    }

    if (href) {
        return <Paper
            component={C1Link}
            href={href}
            tabIndex={tabIndex ?? 0}
            onClick={onClick}
            {...baseProps}
        >
            {children}
        </Paper>
    }

    if (onClick) {
        return <Paper
            component="div"
            role={hasInteractiveChildren ? undefined : 'button'}
            tabIndex={hasInteractiveChildren ? tabIndex : (tabIndex ?? 0)}
            onClick={onClick}
            onKeyDown={hasInteractiveChildren ? undefined : handleKeyDown}
            {...baseProps}
        >
            {children}
        </Paper>
    }

    return <Paper tabIndex={tabIndex} {...baseProps}>
        {children}
    </Paper>
}
